import { AnimatePresence, motion } from "framer-motion"
import { useLayoutEffect, useRef, useState } from "react"
import { TRANSITION } from "@/shared/lib/motion"

/**
 * Подпись таба, у которой меняется сам текст («Черновики» ⇄ «Не опубликованные»). Ширина
 * едет по замеру нового текста, а старый и новый текст наплывают друг на друга. Подложка и
 * соседние табы идут за шириной кнопки в том же кадре, поэтому ничего не прыгает.
 */
export function TabText({ text }: { text: string }) {
	// Ширину берём с невидимой копии текущего текста. Ссылка на сам текст не годится: пока
	// старый текст уходит, она может указывать на него, и ширина застревает от старой подписи.
	const sizer = useRef<HTMLSpanElement>(null)
	const [width, setWidth] = useState<number | undefined>(undefined)
	// Замер синхронно после смены текста, до отрисовки: не ждём кадра и ResizeObserver.
	useLayoutEffect(() => {
		if (sizer.current) setWidth(sizer.current.offsetWidth)
	}, [text])
	// Наблюдатель нужен только для догрузки шрифта, когда текст тот же, а ширина другая.
	useLayoutEffect(() => {
		const element = sizer.current
		if (!element) return
		const observer = new ResizeObserver(() => setWidth(element.offsetWidth))
		observer.observe(element)
		return () => observer.disconnect()
	}, [])
	return (
		// Ширина ведётся CSS-переходом, а не framer-motion: итоговое значение задано прямо в
		// стиле и не застревает, если анимацию прервали на полпути.
		<span
			style={{ width }}
			className="relative inline-flex overflow-hidden whitespace-nowrap transition-[width] duration-[320ms] ease-[var(--ease-out-soft)]"
		>
			<span ref={sizer} aria-hidden="true" className="invisible absolute whitespace-nowrap">
				{text}
			</span>
			<AnimatePresence initial={false} mode="popLayout">
				<motion.span
					key={text}
					initial={{ opacity: 0 }}
					animate={{ opacity: 1, transition: TRANSITION.slow }}
					exit={{ opacity: 0, transition: TRANSITION.fast }}
					className="block"
				>
					{text}
				</motion.span>
			</AnimatePresence>
		</span>
	)
}

/**
 * Счётчик рядом с подписью таба. Приходит позже табов: раскрывается по ширине, а подложка и
 * соседние табы едут за ним, а не прыгают.
 */
export function TabCount({ value }: { value: number | undefined }) {
	return (
		<AnimatePresence initial={false}>
			{value !== undefined ? (
				<motion.span
					initial={{ width: 0, opacity: 0 }}
					animate={{ width: "auto", opacity: 1 }}
					transition={TRANSITION.slow}
					className="overflow-hidden"
				>
					<span className="block pl-1.5 text-xs whitespace-nowrap text-muted-foreground tabular-nums">
						{value}
					</span>
				</motion.span>
			) : null}
		</AnimatePresence>
	)
}
